/**
 * FinancePage — 财务管理（收入 / 产品销量 / 退款统计 / 退款记录 / BONUS 发放）
 *
 * 接真（ADM-FIN-S001~S006）：
 *   - 收入统计 KPI / App 营收排行 → useIncomeStats + useFinanceIncomeByApp（GET /finance/stats/income，原币分币种）
 *   - 退款统计 KPI → useRefundStats（GET /finance/stats/refund，passive_refund_share + by_currency）
 *   - 产品销量 TOP / 明细 → useFinance（GET /finance/product-sales，by_currency）
 *   - 分维度面板 → useFinance（GET /finance/stats/by-platform | by-channel）
 *   - 退款记录 → useRefunds（GET /finance/refunds，webhook 只读镜像）
 *   - BONUS 发放 → GrantBonusForm（POST /finance/grant-bonus；reason_i18n en 必填，展示语言 server 派生）
 *   - 交易归属 → TransactionImportQueue（ADM-FIN-S009）
 *   - 事件闭环 → AdaptyEventReviewQueue（ADM-FIN-S010 GET/POST，不触发重放）
 * 多币种原币不归一（张培 #119）：顶栏 + 各统计 tab 用 CurrencyTabs 切币种，按 by_currency 独立分组取值，
 *   **绝不**用单一 EUR × share 合成（原型旧 mock 的 `* cur.share` 是 P-40 反模式，已删）。
 * 删除的硬编码魔法常量：1142380（累计收入 / App 占比分母）/ 28914·12408·39.48（收入 KPI）/
 *   1408·1802·24720·62.4·2.1（退款 KPI）/ 产品销量 TOP 7 行字面量 → 全切 income/refund/finance 真值。
 *   ARPPU / LTV 卡（无 income-stats 字段、属 Dashboard USD 口径）随之移除，不在原币财务页杜撰。
 *
 * 退款只读镜像（契约 A1 §4 + 既裁决）：admin 退款记录 = vendor webhook 只读镜像，无中间态、无审批写路径，
 *   恒 COMPLETED/PASSIVE。原型「通过/驳回退款」「批准退款/驳回」是 contract-mismatch（P-29）→ 已移除，
 *   RefundRecords 行右键菜单去掉通过/驳回项、RefundDetail 抽屉去掉审批按钮（见 docs/plans §五）。
 *
 * 收入趋势图沿用 Dashboard 真值（revenueBreakdown，USD 展示口径）；退款率趋势 / 退款原因占比无独立端点
 *   （逐日退款率分母 + 退款原因分组均 NOT-in-scope，见 docs/plans §五）→ 空态占位，不渲染魔法分母（旧 /18000 已删）/ 魔法 KPI。
 *
 * ADM-FIN-S008「退款告警」已由 dev-docs 裁决移出本轮 MVP，后端不暴露路由；按设计 SSOT 改为中性 S008
 *   推迟说明卡（非红色告警 banner）。
 */

import { useEffect, useMemo, useState } from 'react';
import { useAppScope } from '~/app/app-scope-store';
import { useFinanceTimeStore } from '~/app/finance-time-store';
import {
  useApps,
  useDashboard,
  useFinance,
  useFinanceIncomeByApp,
  useIncomeStats,
  useRefundStats,
  useRefundTrend,
  useRefundsPage,
} from '~/data/queries';
import type { FinanceRow, ProductSales, RefundRow } from '~/data/queries';
import { currencySymbol, fmtDateTime } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { AppIcon } from '~/ui/AppIcon';
import { ContextMenu } from '~/ui/ContextMenu';
import { DateRangeButton } from '~/ui/DatePicker';
import { FilterButton } from '~/ui/FilterButton';
import { Pagination } from '~/ui/Pagination';
import { SearchInput } from '~/ui/SearchInput';
import { ChartSkeleton, KpiSkeleton, SectionSkeleton } from '~/ui/Skeleton';
import { Table } from '~/ui/Table';
import { toast } from '~/ui/Toast';
import { Charts } from '~/ui/charts';
import { AdaptyEventReviewQueue } from './AdaptyEventReviewQueue';
import { GrantBonusForm } from './GrantBonusForm';
import { TransactionImportQueue } from './TransactionImportQueue';
import { RefundDetail } from './dialogs/RefundDetail';

// 顶部维度切换：app | platform | channel
type Dim = 'app' | 'platform' | 'channel';

/** range 段标签 → period 代码（年初至今 → ytd，其余 24h/7d/30d/90d 直用） */
function periodCode(range: string): string {
  return range === '年初至今' ? 'ytd' : range;
}

// 千分位整数（统计卡 / 表格用，统一口径）
const fmtInt = (n: number) => Math.round(n).toLocaleString();

const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';

/**
 * CurrencyTabs — 币种切换选项卡（§8 多币种 by_currency 共用底座，S001/S002/S005/S006）。
 * options = 三端点 union 的真实币种全集；原币不归一、不跨币种相加。
 */
function CurrencyTabs({
  currencies,
  ccy,
  setCcy,
}: {
  currencies: string[];
  ccy: string;
  setCcy: (c: string) => void;
}) {
  if (currencies.length === 0) return null;
  return (
    <div className="hstack" style={{ gap: 8 }}>
      <span style={{ fontSize: 11, color: 'var(--fg-muted)' }}>按币种</span>
      <div className="seg" title="§8 多币种 MVP · 响应按原币 by_currency 分组返回 · 不跨币种相加">
        {currencies.map((c) => (
          <button key={c} className={ccy === c ? 'on' : ''} onClick={() => setCcy(c)}>
            {c}
          </button>
        ))}
      </div>
      <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
        按原币统计 · 不涉及汇率归一
      </span>
    </div>
  );
}

/**
 * ScopeBanner — 单 App scope 提示条（design finance.jsx:21-37，渲染于 Revenue/Refund/ProductSales 三 tab 顶部）。
 * 仅当选中具体 App（appId!=='all'）渲染，标明各统计已按本 App 维度统计。
 *
 * 接真偏离（approach a，见 docs/plans §五）：design 的「营收占全平台 N%」「退款率 M%」两个数值在 impl
 * 不可白嫖——apps DTO 不含 revenue/refundRate 聚合列（queries.ts:163 明确为 0 默认、非真值），且单 App
 * scope 下 useFinanceIncomeByApp enabled=false 不 fan-out 取占比分母。诚实只展示可得字段（App 名 +
 * app_scope=<id>），缺失的占比/退款率显式记账 NOT-in-scope，绝不用 0 默认伪造百分比（P-40 反模式）。
 */
function ScopeBanner({ app }: { app: { id: string; name: string } }) {
  if (app.id === 'all') return null;
  return (
    <div
      className="card-flat"
      style={{ padding: '8px 12px', background: 'var(--accent-soft)', borderColor: 'transparent' }}
    >
      <div className="hstack" style={{ gap: 8, fontSize: 11.5 }}>
        <Icons.IconChart size={12} style={{ color: 'var(--accent)' }} />
        <span style={{ color: 'var(--fg)' }}>
          已按 <span className="strong">{app.name}</span> 维度统计（app_scope=
          <span className="mono">{app.id}</span>）
        </span>
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 10, color: 'var(--fg-muted)' }}>
          切换顶部 App 选择器可改变口径
        </span>
      </div>
    </div>
  );
}

/**
 * FinanceViewSkeleton — 路由级骨架，按当前 tab 拼出与真实视图**同形**的占位（finding B）。
 * 不复用 PageSkeleton page="finance"（那是 filter+table 形，与默认收入视图的 KPI+图表+排行 错位会位移），
 * 改由各 building block inline 组合：币种条占位 + 5 KPI 卡 + 视图各自的图表/表格区。
 */
function FinanceViewSkeleton({ tab }: { tab: string }) {
  // 仅数据面板占位：渲染在真实页面 .page 容器内（页头/tab 栏之下），故用 Fragment，不再自带 .page/.page-enter（避免嵌套页壳 + 双重淡入）
  return (
    <>
      {/* 币种切换条占位（CurrencyTabs 同位高度，避免数据到达后下移） */}
      <div style={{ height: 22 }} />
      {/* 5 张 KPI 卡（收入 / 退款 / 产品销量三视图首屏均为 5 卡） */}
      <KpiSkeleton count={5} />
      {tab === 'products' ? (
        // 产品销量：单张明细表（用 SectionSkeleton 占位卡体，避免引入 table 形位移）
        <SectionSkeleton rows={6} />
      ) : (
        // 收入 / 退款：左大图右窄卡（趋势图 + App 营收排行 / 退款原因占比），下接一张表/卡
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
              gap: 12,
            }}
          >
            <ChartSkeleton height={240} />
            <ChartSkeleton height={240} title={100} />
          </div>
          <SectionSkeleton rows={5} />
        </>
      )}
    </>
  );
}

export default function FinancePage() {
  const apps = useApps().data;
  const { appId } = useAppScope();
  // 当前 App：appId==='all' → 聚合视图对象，否则取对应 App（与原型 app prop 同形）
  const app: { id: string; name: string } =
    appId === 'all'
      ? { id: 'all', name: '全部 App' }
      : (apps.find((a) => a.id === appId) ?? { id: 'all', name: '全部 App' });

  const [tab, setTab] = useState('revenue');
  const [range, setRange] = useState('30d');
  // 自定义日期区间（DateRangeButton）；非空覆盖快捷档 range
  const [customRange, setCustomRange] = useState<[Date, Date] | null>(null);
  const [dim, setDim] = useState<Dim>('app'); // app | platform | channel

  // 页头时间窗 → 写入 finance-time-store，各面板 finance hook 内部读取并随之 refetch（自定义区间优先）
  const setFinanceTime = useFinanceTimeStore((s) => s.setTime);
  useEffect(() => {
    setFinanceTime(
      customRange
        ? { from: customRange[0].toISOString(), to: customRange[1].toISOString() }
        : { period: periodCode(range) },
    );
  }, [range, customRange, setFinanceTime]);

  // 多币种币种全集 = 收入 / 退款 / 分维度·产品销量三类响应的 currencies union（原币不归一 #119）
  const incomeQuery = useIncomeStats();
  const refundQuery = useRefundStats();
  const finQuery = useFinance();
  const incomeData = incomeQuery.data;
  const refundData = refundQuery.data;
  const finData = finQuery.data;
  // useMemo 稳定引用：currencies 进了下方 effect 依赖，每渲染新建数组会触发无限重渲染（checklist #1）。
  const currencies = useMemo(
    () =>
      [
        ...new Set([...incomeData.currencies, ...refundData.currencies, ...finData.currencies]),
      ].sort(),
    [incomeData.currencies, refundData.currencies, finData.currencies],
  );
  // 按币种共用选项卡：默认首个币种；币种集合到达 / 变化后纠正越界选择
  const [ccy, setCcy] = useState<string | null>(null);
  useEffect(() => {
    if (currencies.length > 0 && (ccy === null || !currencies.includes(ccy))) {
      setCcy(currencies[0] ?? null);
    }
  }, [ccy, currencies]);
  const currency = ccy ?? currencies[0] ?? '';

  const tabs = [
    { id: 'revenue', label: '收入统计' },
    { id: 'products', label: '产品销量' },
    { id: 'refund', label: '退款统计' },
    { id: 'records', label: '退款记录' },
    { id: 'attribution', label: '交易归属' },
    { id: 'event-reviews', label: '事件闭环' },
    { id: 'bonus', label: 'BONUS 发放' },
  ];

  // 路由级骨架只门控**当前 tab 真正依赖**的查询，不被非活跃 tab 的慢查询拖累（finding A）：
  //   revenue  → income(KPI) + finance(内嵌产品 TOP)；趋势图 useDashboard / 排行 useFinanceIncomeByApp
  //              各自有空态兜底，不进首屏门控（慢也不挡 KPI/产品 TOP 出现）
  //   products → finance（产品销量明细）
  //   refund   → refund + income（被动退款率由 refund_count ÷ charge_orders 派生，两者都需到）
  //   records  → useRefunds 自带分页加载态（RefundRecords 内处理，不进路由门控）
  //   bonus    → BONUS KPI / 表单自带加载态（BonusGrant 内处理，不进路由门控）
  const activeViewLoading =
    tab === 'revenue'
      ? incomeQuery.isLoading || finQuery.isLoading
      : tab === 'products'
        ? finQuery.isLoading
        : tab === 'refund'
          ? refundQuery.isLoading || incomeQuery.isLoading
          : false;

  return (
    <div className="page page-enter">
      <div className="page-header">
        <div>
          <h1 className="page-title">财务管理</h1>
          <div className="page-sub">
            {app.id === 'all' ? '全平台聚合' : `${app.name} 维度`} · 多币种 by_currency
            分组（原币口径 · 不归一）· 渠道闭集 IAP / Stripe
          </div>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          <div className="seg" title="ADM-FIN-001 分维度统计">
            <button className={dim === 'app' ? 'on' : ''} onClick={() => setDim('app')}>
              按 App
            </button>
            <button className={dim === 'platform' ? 'on' : ''} onClick={() => setDim('platform')}>
              按平台
            </button>
            <button className={dim === 'channel' ? 'on' : ''} onClick={() => setDim('channel')}>
              按渠道
            </button>
          </div>
          <div className="seg">
            {['24h', '7d', '30d', '90d', '年初至今'].map((r) => (
              <button
                key={r}
                className={!customRange && range === r ? 'on' : ''}
                onClick={() => {
                  setRange(r);
                  setCustomRange(null); // 选快捷档清除自定义区间
                }}
              >
                {r}
              </button>
            ))}
          </div>
          {/* 自定义日期区间：选定后覆盖快捷档（写 finance-time-store from/to → 各面板 refetch） */}
          <DateRangeButton
            defaultQuick="30d"
            label="自定义"
            value={customRange ?? undefined}
            onChange={(r) => setCustomRange(r)}
          />
          {/* 导出：契约无 finance 导出端点 → disabled + NOT-in-scope（不假成功 toast） */}
          <button
            className="btn"
            disabled
            title={NOT_IN_SCOPE_TITLE}
            style={{ cursor: 'not-allowed' }}
          >
            <Icons.IconDownload size={13} />
            导出
          </button>
        </div>
      </div>

      <div style={{ borderBottom: '1px solid var(--border)', display: 'flex', gap: 0 }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              border: 0,
              background: 'transparent',
              padding: '9px 14px',
              font: 'inherit',
              fontSize: 12.5,
              cursor: 'pointer',
              color: tab === t.id ? 'var(--fg-strong)' : 'var(--fg-muted)',
              fontWeight: tab === t.id ? 500 : 400,
              borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
              marginBottom: -1,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* 数据面板：仅此处按当前 tab 真实依赖门控骨架；页头(维度/区间/导出)+ tab 栏在上方常驻可交互，
          慢/挂查询不再困住运营，也不挡切 tab（finding：keep finance chrome mounted while loading）。 */}
      {activeViewLoading ? (
        <FinanceViewSkeleton tab={tab} />
      ) : (
        <>
          {tab === 'revenue' && (
            <Revenue
              dim={dim}
              currency={currency}
              currencies={currencies}
              setCcy={setCcy}
              app={app}
              onViewProducts={() => setTab('products')}
            />
          )}
          {tab === 'products' && (
            <ProductSalesTab
              currency={currency}
              currencies={currencies}
              setCcy={setCcy}
              app={app}
            />
          )}
          {tab === 'refund' && (
            <Refund currency={currency} currencies={currencies} setCcy={setCcy} app={app} />
          )}
          {tab === 'records' && <RefundRecords currencies={currencies} />}
          {tab === 'attribution' && <TransactionImportQueue />}
          {tab === 'event-reviews' && <AdaptyEventReviewQueue />}
          {tab === 'bonus' && <BonusGrant />}
        </>
      )}
    </div>
  );
}

// ADM-FIN-001 分维度切换 · 顶部维度面板（by_currency 套层各维度）
function DimPanel({ dim, currency }: { dim: Dim; currency: string }) {
  const fin = useFinance().data;
  if (dim === 'app') return null;
  // 原币不归一（#119）：取当前选中币种组的维度行（缺则空）
  const cur = fin.byCurrency[currency];
  const data: FinanceRow[] = (dim === 'platform' ? cur?.byPlatform : cur?.byChannel) ?? [];
  const sym = currencySymbol(currency);
  const title = dim === 'platform' ? '按平台维度' : '按支付渠道维度';
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="hstack" style={{ marginBottom: 10 }}>
        <div style={{ fontWeight: 500, fontSize: 13.5 }}>{title}</div>
        <span className="mono muted" style={{ marginLeft: 8, fontSize: 11 }}>
          · 本期累计 · {dim === 'channel' ? '渠道闭集 IAP · Stripe · ' : ''}币种 {currency}
        </span>
        <div style={{ flex: 1 }} />
        <span className="chip chip-info" style={{ fontSize: 10 }}>
          ADM-FIN-S005 · by_currency
        </span>
      </div>
      <Table className="tbl">
        <thead>
          <tr>
            <th>{dim === 'platform' ? '平台' : '渠道'}</th>
            <th>币种</th>
            <th className="num">订单数</th>
            <th className="num">独立用户</th>
            <th className="num">收入</th>
            <th className="num">占比</th>
            <th className="num">被动退款率</th>
            <th style={{ width: 110 }} />
          </tr>
        </thead>
        <tbody>
          {data.map((r, i) => (
            <tr key={r.key}>
              <td>
                <div className="hstack" style={{ gap: 8 }}>
                  <span className="mono muted" style={{ width: 16 }}>
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span style={{ fontWeight: 500, fontSize: 12.5 }}>{r.key}</span>
                </div>
              </td>
              <td>
                <span className="chip mono" style={{ fontSize: 10 }}>
                  {currency}
                </span>
              </td>
              <td className="num">{r.orders.toLocaleString()}</td>
              <td className="num">{r.users.toLocaleString()}</td>
              <td className="num">
                {sym}
                {fmtInt(r.revenue)}
              </td>
              <td className="num mono">{r.share.toFixed(1)}%</td>
              <td
                className="num mono"
                style={{
                  color:
                    r.refundRate > 3.0
                      ? 'var(--neg)'
                      : r.refundRate > 2.0
                        ? 'var(--warn)'
                        : 'var(--fg-muted)',
                }}
              >
                {r.refundRate.toFixed(2)}%
              </td>
              <td>
                <div
                  style={{
                    height: 6,
                    borderRadius: 3,
                    background: 'var(--bg-subtle)',
                    position: 'relative',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: `${r.share}%`,
                      background: 'var(--accent)',
                    }}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <div style={{ marginTop: 8, fontSize: 10.5, color: 'var(--fg-faint)' }}>
        多币种时每个 (currency, dimension) 组合各为一行 · 切换顶部「按币种」查看其他原币子表 ·
        不跨币种相加
      </div>
    </div>
  );
}

function Revenue({
  dim = 'app',
  currency,
  currencies,
  setCcy,
  app,
  onViewProducts,
}: {
  dim?: Dim;
  currency: string;
  currencies: string[];
  setCcy: (c: string) => void;
  app: { id: string; name: string };
  onViewProducts: () => void;
}) {
  // ADM-FIN-S001 收入 KPI：取当前选中币种组真值（原币不归一，绝不跨币种合成）
  const income = useIncomeStats().data.byCurrency[currency];
  const apps = useApps().data;
  // F1：App 营收排行是全平台视图专属（跨 App 横向对比仅 'all' scope 有意义）
  const { appId } = useAppScope();
  const isAllScope = appId === 'all';
  // 收入趋势图沿用 Dashboard 真值（revenueBreakdown，USD 展示口径，渠道闭集 IAP/Stripe）
  const breakdown = useDashboard().data.revenueBreakdown as unknown as Array<
    Record<string, string | number>
  >;
  // App 营收排行：fan-out incomeStats(appId) 取各 App 该币种真实收入（替代旧 `a.revenue/1142380` 魔法占比）；
  // 单 App scope 下 hook enabled=false → byApp 为空，下方渲染「排行仅全平台视图可见」空态（不请求其它 App）
  const byApp = useFinanceIncomeByApp().data;
  const appRows = byApp
    .map((a) => ({
      id: a.id,
      name: a.name,
      revenue: a.byCurrency[currency] ?? 0,
      unavailable: a.unavailable,
    }))
    .sort((x, y) => y.revenue - x.revenue);
  // F4：有 App 收入获取失败 → 占比分母残缺，**不**用部分集重算 100% 占比（隐藏占比 + 标数据不完整）。
  const hasUnavailableApp = appRows.some((a) => a.unavailable);
  const appTotal = appRows.reduce((s, a) => s + (a.unavailable ? 0 : a.revenue), 0);
  const appById = new Map(apps.map((a) => [a.id, a]));
  const sym = currencySymbol(currency);

  const kpis = [
    {
      label: '充值订单数',
      v: income ? fmtInt(income.chargeOrders) : '—',
      code: undefined as string | undefined,
    },
    { label: '充值用户数', v: income ? fmtInt(income.chargeUsers) : '—', code: undefined },
    {
      label: '人均充值次数',
      v: income ? income.avgChargeCountPerUser.toFixed(2) : '—',
      code: undefined,
    },
    { label: '累计收入', v: income ? `${sym}${fmtInt(income.totalRevenue)}` : '—', code: currency },
    {
      label: '平均客单价',
      v: income ? `${sym}${income.avgOrderValue.toFixed(2)}` : '—',
      code: currency,
    },
  ];

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <CurrencyTabs currencies={currencies} ccy={currency} setCcy={setCcy} />
      <ScopeBanner app={app} />
      {dim !== 'app' && <DimPanel dim={dim} currency={currency} />}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {kpis.map((s, i) => (
          <div key={i} className="card" style={{ padding: 14 }}>
            <div className="stat-label">
              {s.label}
              {s.code && (
                <span
                  className="mono"
                  style={{ marginLeft: 5, fontSize: 9.5, color: 'var(--fg-faint)' }}
                >
                  {s.code}
                </span>
              )}
            </div>
            <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
              {s.v}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
          gap: 12,
        }}
      >
        <div className="card" style={{ padding: 14 }}>
          <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
            <div>
              <div style={{ fontWeight: 500, fontSize: 13.5 }}>收入趋势 · 按支付渠道</div>
              <div style={{ color: 'var(--fg-muted)', fontSize: 11.5 }}>
                IAP · Stripe · Dashboard 展示口径
              </div>
            </div>
            <div className="hstack" style={{ gap: 10, fontSize: 11, color: 'var(--fg-muted)' }}>
              <span className="hstack" style={{ gap: 4 }}>
                <span
                  style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--chart-1)' }}
                />
                IAP
              </span>
              <span className="hstack" style={{ gap: 4 }}>
                <span
                  style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--chart-2)' }}
                />
                Stripe
              </span>
            </div>
          </div>
          {/* 去假（对抗审查 round-3 P1）：breakdown=useDashboard().revenueBreakdown 恒全平台口径，与 App 营收排行
              同组（line 569 有 isAllScope 闸）。单 App scope 下该图挂在 per-App ScopeBanner 之下却画全站趋势 =
              全平台冒充本 App 表现（同 Paywall/Apps 模式）→ 仅全平台 scope 渲染；per-App scope 诚实占位
              （per-app revenue_trend 需后端按 app 提供）。 */}
          {isAllScope ? (
            <Charts.StackedBarChart
              data={breakdown}
              keys={['iap', 'stripe']}
              colors={['var(--chart-1)', 'var(--chart-2)']}
              height={240}
            />
          ) : (
            <div
              className="hstack"
              style={{
                fontSize: 11.5,
                color: 'var(--fg-faint)',
                minHeight: 240,
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
              }}
            >
              单 App 收入趋势需后端按 app 提供，未接入前不展示（Dashboard 趋势为全平台口径）
            </div>
          )}
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontWeight: 500, fontSize: 13.5, marginBottom: 10 }}>
            App 营收排行{' '}
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
              {currency}
            </span>
            {/* 排行恒全平台口径,与上方按 App 重算 KPI 不同口径（design finance.jsx:229 chip-info）；仅全平台 scope 展示排行时标注 */}
            {isAllScope && (
              <span
                className="chip chip-info"
                style={{ fontSize: 9, marginLeft: 6 }}
                title="排行始终为全平台口径,与上方按 App 重算的 KPI 不同口径"
              >
                全平台口径
              </span>
            )}
          </div>
          {/* F1：营收排行仅全平台视图可见——单 App scope 下隐藏排行（不请求 / 渲染其它 App 收入），显空态 */}
          {!isAllScope ? (
            <div
              className="vstack"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                minHeight: 150,
                color: 'var(--fg-faint)',
                textAlign: 'center',
              }}
            >
              <Icons.IconInfo size={20} style={{ color: 'var(--fg-faint)' }} />
              <span style={{ fontSize: 11.5 }}>
                排行仅全平台视图可见 · 切顶栏 App 至「全部 App」查看跨 App 营收对比
              </span>
            </div>
          ) : (
            <>
              {/* F4：部分 App 收入获取失败 → 占比口径不完整，明确标注，不以残缺集伪装完整 */}
              {hasUnavailableApp && (
                <div
                  className="hstack"
                  style={{ gap: 6, marginBottom: 8, fontSize: 10.5, color: 'var(--warn)' }}
                >
                  <Icons.IconAlert size={11} style={{ flexShrink: 0 }} />
                  <span>部分 App 收入获取失败 · 占比口径不完整，已隐藏占比列</span>
                </div>
              )}
              <Table className="tbl" style={{ marginBottom: -1 }}>
                <thead>
                  <tr>
                    <th>App</th>
                    <th className="num">收入</th>
                    {!hasUnavailableApp && <th className="num">占比</th>}
                  </tr>
                </thead>
                <tbody>
                  {appRows.map((a, i) => {
                    const meta = appById.get(a.id);
                    return (
                      <tr key={a.id}>
                        <td>
                          <div className="hstack" style={{ gap: 8 }}>
                            <span className="mono muted" style={{ width: 16 }}>
                              {String(i + 1).padStart(2, '0')}
                            </span>
                            {meta && <AppIcon app={meta} size={{ box: 18, font: 9, radius: 4 }} />}
                            <span style={{ fontSize: 12 }}>{a.name}</span>
                          </div>
                        </td>
                        <td className="num">
                          {a.unavailable ? (
                            <span style={{ color: 'var(--warn)', fontSize: 11 }}>数据异常</span>
                          ) : (
                            <>
                              {sym}
                              {fmtInt(a.revenue)}
                            </>
                          )}
                        </td>
                        {!hasUnavailableApp && (
                          <td className="num">
                            {appTotal > 0 ? ((a.revenue / appTotal) * 100).toFixed(1) : '0.0'}%
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </>
          )}
        </div>
      </div>

      <ProductTop currency={currency} onViewProducts={onViewProducts} />
    </div>
  );
}

// 产品销量 TOP（收入统计 tab 内嵌精简版，取 useFinance 当前币种组真值；明细在「产品销量」tab）
// 渠道列删除：ProductSales DTO（A1 §6）无 channel 字段（adapter 不产出），与 S006 明细表一致；
// 趋势 Sparkline 列删除：原型用 Math.sin 合成 mock（design finance.jsx:309），无逐产品趋势端点 → 不杜撰。
// 「查看全部」由原型假 toast 改为真实 tab 切换（onViewProducts → setTab('products')），无跨页路由。
function ProductTop({
  currency,
  onViewProducts,
}: { currency: string; onViewProducts: () => void }) {
  const rows: ProductSales[] = useFinance().data.byCurrency[currency]?.productSales ?? [];
  const top = rows.slice(0, 7);
  const sym = currencySymbol(currency);
  return (
    <div className="card" style={{ padding: 14 }}>
      <div className="hstack" style={{ justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontWeight: 500, fontSize: 13.5 }}>
          产品销量 TOP · 充值套餐{' '}
          <span className="mono" style={{ fontSize: 10, color: 'var(--fg-faint)' }}>
            {currency}
          </span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={onViewProducts}>
          查看全部
          <Icons.IconChevRight size={11} />
        </button>
      </div>
      <Table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 36 }}>#</th>
            <th>产品名称</th>
            <th>类型</th>
            <th className="num">价格 ({currency})</th>
            <th className="num">购买次数</th>
            <th className="num">独立用户</th>
            <th className="num">累计收入</th>
            <th className="num">占比</th>
          </tr>
        </thead>
        <tbody>
          {top.map((r, i) => (
            <tr key={r.name}>
              <td className="mono muted">{String(i + 1).padStart(2, '0')}</td>
              <td style={{ fontWeight: 500 }}>{r.name}</td>
              <td>
                <span className="chip" style={{ fontSize: 10 }}>
                  {r.kind}
                </span>
              </td>
              <td className="num">{r.price != null ? `${sym}${r.price.toFixed(2)}` : '—'}</td>
              <td className="num">{r.orders.toLocaleString()}</td>
              <td className="num">{r.users.toLocaleString()}</td>
              <td className="num">
                {sym}
                {fmtInt(r.revenue)}
              </td>
              <td className="num">{r.sharePct.toFixed(1)}%</td>
            </tr>
          ))}
          {top.length === 0 && (
            <tr>
              <td
                colSpan={8}
                style={{ textAlign: 'center', color: 'var(--fg-faint)', padding: 18 }}
              >
                当前币种（{currency}）暂无产品销量数据
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

function Refund({
  currency,
  currencies,
  setCcy,
  app,
}: {
  currency: string;
  currencies: string[];
  setCcy: (c: string) => void;
  app: { id: string; name: string };
}) {
  // ADM-FIN-S002 退款 KPI：当前币种组真值 + 顶层 passive_refund_share（占比）
  const refund = useRefundStats().data;
  const cur = refund.byCurrency[currency];
  // 被动退款率（S005 口径：维度内 REFUND笔数÷PURCHASE笔数）无顶层标量 → 用同币种 refundStats.refund_count
  // ÷ incomeStats.charge_orders 派生（均为真值计数，非合成）；分母 0 → '—'。
  const income = useIncomeStats().data.byCurrency[currency];
  const passiveRate =
    cur && income && income.chargeOrders > 0 ? (cur.refundCount / income.chargeOrders) * 100 : null;
  const sym = currencySymbol(currency);
  // ADM-FIN 退款率逐日趋势（GET /finance/stats/refund-trend，随 App scope + 页头时间窗）
  const refundTrend = useRefundTrend().data;

  const cards = [
    {
      label: '退款用户数',
      v: cur ? cur.refundUsers.toLocaleString() : '—',
      code: undefined as string | undefined,
      tip: undefined as string | undefined,
    },
    {
      label: '退款次数',
      v: cur ? cur.refundCount.toLocaleString() : '—',
      code: undefined,
      tip: undefined,
    },
    {
      label: '累计退款金额',
      v: cur ? `${sym}${fmtInt(cur.totalRefund)}` : '—',
      code: currency,
      tip: undefined,
    },
    {
      label: '被动退款占比',
      v:
        refund.passiveRefundShare === null
          ? '—'
          : `${(refund.passiveRefundShare * 100).toFixed(1)}%`,
      code: undefined,
      tip: '被动退款占比 = Refund 笔数 ÷ 全部退款笔数（本表全为 vendor 被动退款，有退款=100% / 无=0%）',
    },
    {
      label: '被动退款率',
      v: passiveRate === null ? '—' : `${passiveRate.toFixed(2)}%`,
      code: undefined,
      tip: '被动退款率 = 该币种 REFUND 笔数 ÷ PURCHASE 笔数（S005 口径，由 refund/income 真值计数派生）',
    },
  ];

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <CurrencyTabs currencies={currencies} ccy={currency} setCcy={setCcy} />
      <ScopeBanner app={app} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {cards.map((s, i) => (
          <div key={i} className="card" style={{ padding: 14 }} title={s.tip}>
            <div className="stat-label">
              {s.label}
              {s.code && (
                <span
                  className="mono"
                  style={{ marginLeft: 5, fontSize: 9.5, color: 'var(--fg-faint)' }}
                >
                  {s.code}
                </span>
              )}
              {s.tip && (
                <Icons.IconInfo
                  size={10}
                  style={{ verticalAlign: 'middle', marginLeft: 4, color: 'var(--fg-faint)' }}
                />
              )}
            </div>
            <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
              {s.v}
            </div>
          </div>
        ))}
      </div>

      {/* ADM-FIN-S008 分维度退款告警本轮推迟 · S002 仅统计展示，不含阈值 / 越界状态（设计 SSOT 中性说明卡，非红色告警 banner） */}
      <div className="card-flat" style={{ padding: '8px 12px', background: 'var(--bg-subtle)' }}>
        <div className="hstack" style={{ gap: 8, fontSize: 11.5, color: 'var(--fg-muted)' }}>
          <Icons.IconInfo size={11} style={{ color: 'var(--info)' }} />
          <span>
            分维度退款告警（ADM-FIN-S008）本轮推迟 · 本页仅统计展示，不展示阈值 / 越界状态
          </span>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.6fr) minmax(0, 1fr)',
          gap: 12,
        }}
      >
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontWeight: 500, fontSize: 13.5, marginBottom: 10 }}>
            退款率趋势 · 30 天
          </div>
          {/* 接真：逐日退款率 = refund_count ÷ charge_count（GET /finance/stats/refund-trend，按 occurred_at 分日，honor 时间窗）*/}
          {refundTrend.length === 0 ? (
            <div
              className="vstack"
              style={{
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                minHeight: 200,
                color: 'var(--fg-faint)',
                textAlign: 'center',
              }}
            >
              <Icons.IconInfo size={20} style={{ color: 'var(--fg-faint)' }} />
              <span style={{ fontSize: 11.5 }}>所选时间窗内暂无退款 / 充值数据</span>
            </div>
          ) : (
            <Charts.AreaChart
              data={refundTrend.map((p) => ({ date: p.date, rate: p.refund_rate * 100 }))}
              height={200}
              series={[{ key: 'rate', color: 'var(--neg)', label: '退款率' }]}
              yFormat={(v) => `${Number(v).toFixed(1)}%`}
            />
          )}
        </div>
        {/* 退款原因占比：dev-docs 无 refund-reason 分组端点（NOT-in-scope，见 docs/plans §五）→ 空态占位，不渲染魔法占比 */}
        <div className="card" style={{ padding: 14 }}>
          <div style={{ fontWeight: 500, fontSize: 13.5, marginBottom: 10 }}>退款原因占比</div>
          <div
            className="vstack"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              minHeight: 150,
              color: 'var(--fg-faint)',
              textAlign: 'center',
            }}
          >
            <Icons.IconInfo size={20} style={{ color: 'var(--fg-faint)' }} />
            <span style={{ fontSize: 11.5 }}>
              退款原因分组待契约（dev-docs 无对应端点 · NOT-in-scope）
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RefundRecords({ currencies }: { currencies: string[] }) {
  // ADM-FIN-S003 offset 分页（cursor「加载更多」→ offset 分页器）：page 直驱后端，total 真实总数 → 跳任意页。
  const appId = useAppScope((s) => s.appId);
  const pageSize = useAppScope((s) => s.tablePageSize);
  const [page, setPage] = useState(1);
  const {
    data: refunds,
    total: refundTotal,
    isLoading: refundsLoading,
  } = useRefundsPage({ page, limit: pageSize });
  // App scope / 每页档位变化 → 回第一页（offset 页码与这两者绑定）。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 该 effect 专门在 appId/pageSize 变化时重置页码。
  useEffect(() => {
    setPage(1);
  }, [appId, pageSize]);
  // 越界页 clamp：切 App / total 收缩后停在旧页码 → 后端返 data=[] 但 total>0，加载完成后 clamp 回末页重取，不卡空表。
  useEffect(() => {
    if (refundsLoading) return;
    const maxPage = Math.max(1, Math.ceil(refundTotal / pageSize));
    if (page > maxPage) setPage(maxPage);
  }, [refundTotal, pageSize, page, refundsLoading]);
  const apps = useApps().data;
  // 退款单点击 → 打开退款详情抽屉（原型 window.openDialog('refund-detail')）
  const [detail, setDetail] = useState<RefundRow | null>(null);
  // F2（adversarial r2）：退款 DTO（契约 A1 §4）不提供 channel / reason 字段，且 trigger 恒 PASSIVE、
  // final_result 恒 COMPLETED（webhook 只读镜像，无中间态、无主动退款写路径）。故：
  //   - **移除渠道筛选**（adapter channel='' → 选 IAP/Stripe 必匹配不上，false empty）；
  //   - 类型筛选只保留真值 `被动退款`（adapter REFUND_TRIGGER_ZH 标签；不再给永匹配不上的「主动」）；
  //   - 状态筛选只保留真值 `已退款`（恒 COMPLETED；不再给「处理中 / 已驳回」）。
  // 搜索 + App / 币种 / 状态 / 类型筛选（按 adapter 真值过滤，不暴露契约未提供的维度）
  const [q, setQ] = useState('');
  const [appFilter, setAppFilter] = useState<string[]>([]);
  const [ccyFilter, setCcyFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<string[]>([]);

  // 客户端筛选 over 已加载页；分页由服务端 cursor 驱动（无客户端 slice），筛选不丢未加载页。
  const shown = refunds.filter((r) => {
    if (
      q &&
      !(
        r.id.toLowerCase().includes(q.toLowerCase()) ||
        r.orderId.toLowerCase().includes(q.toLowerCase()) ||
        r.userId.toLowerCase().includes(q.toLowerCase())
      )
    )
      return false;
    if (appFilter.length && !appFilter.includes(r.appId)) return false;
    if (ccyFilter.length && !ccyFilter.includes(r.currency)) return false;
    if (statusFilter.length && !statusFilter.includes(r.status)) return false;
    if (typeFilter.length && !typeFilter.includes(r.type)) return false;
    return true;
  });

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="hstack" style={{ gap: 8 }}>
        <SearchInput
          width={280}
          placeholder="订单号 / 用户 ID"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <FilterButton
          label="App"
          width={180}
          value={appFilter}
          onChange={setAppFilter}
          options={apps.map((a) => ({ value: a.id, label: a.name }))}
        />
        {/* F2：渠道筛选移除——退款 DTO（A1 §4）无 channel 字段，避免给永匹配不上的 IAP/Stripe */}
        <FilterButton
          label="币种"
          width={130}
          value={ccyFilter}
          onChange={setCcyFilter}
          options={currencies.map((c) => ({ value: c, label: c }))}
        />
        {/* F2：状态恒 COMPLETED（A1 §4 无中间态）→ 只给真值「已退款」，不给永匹配不上的处理中/已驳回 */}
        <FilterButton
          label="状态"
          width={150}
          value={statusFilter}
          onChange={setStatusFilter}
          options={[{ value: '已退款', label: '已退款' }]}
        />
        {/* F2：trigger 恒 PASSIVE（A1 §4 无主动退款写路径）→ 只给真值「被动退款」（= adapter REFUND_TRIGGER_ZH 标签） */}
        <FilterButton
          label="类型"
          width={140}
          value={typeFilter}
          onChange={setTypeFilter}
          options={[{ value: '被动退款', label: '被动退款' }]}
        />
        <div style={{ flex: 1 }} />
        <span className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
          共 {refundTotal} 条（本页客户端筛 {shown.length}）
        </span>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <Table className="tbl">
          <thead>
            {/* F2：移除「渠道」「原因」列——退款 DTO（契约 A1 §4）不提供这两个字段，不渲染空白单元格伪装有值 */}
            <tr>
              <th>退款单</th>
              <th>订单</th>
              <th>App</th>
              <th>用户</th>
              <th className="num">金额</th>
              <th>币种</th>
              <th>类型</th>
              <th>状态</th>
              <th>时间</th>
              <th style={{ width: 36 }} />
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} style={{ cursor: 'pointer' }} onClick={() => setDetail(r)}>
                <td className="mono">{r.id}</td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {r.orderId}
                </td>
                <td>
                  <span className="chip" style={{ fontSize: 10 }}>
                    {r.appName}
                  </span>
                </td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {r.userId}
                </td>
                <td className="num mono">
                  {currencySymbol(r.currency)}
                  {r.amount}
                </td>
                <td>
                  <span
                    className="chip mono"
                    style={{ fontSize: 10 }}
                    title="ISO 4217 · 原币不归一"
                  >
                    {r.currency}
                  </span>
                </td>
                <td>
                  {/* F2：trigger 恒 PASSIVE（A1 §4），adapter 标签 = '被动退款'（不再用永匹配不上的 '被动' 比较） */}
                  <span
                    className={`chip ${r.type === '被动退款' ? 'chip-neg' : ''}`}
                    style={{ fontSize: 10 }}
                    title={
                      r.type === '被动退款'
                        ? '被动退款 · vendor 渠道发起（App Store / Adapty webhook），admin 只读镜像'
                        : undefined
                    }
                  >
                    {r.type}
                  </span>
                </td>
                <td>
                  <span
                    className={`chip ${
                      r.status === '已退款'
                        ? 'chip-pos'
                        : r.status === '已驳回'
                          ? 'chip-neg'
                          : 'chip-warn'
                    }`}
                    style={{ fontSize: 10 }}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)' }}>
                  {fmtDateTime(r.createdAt)}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {/* 退款只读镜像（契约 A1 §4）：仅查看 / 复制；无「通过 / 驳回退款」写动作（已移除，见 docs/plans §五） */}
                  <ContextMenu
                    items={[
                      {
                        label: '查看退款详情',
                        icon: 'IconEye',
                        onClick: () => setDetail(r),
                      },
                      {
                        label: '复制货币码',
                        icon: 'IconCopy',
                        onClick: () => {
                          navigator.clipboard?.writeText(r.currency);
                          toast(`已复制 ${r.currency}`, { tone: 'pos', icon: 'IconCopy' });
                        },
                      },
                      {
                        label: '复制退款单号',
                        icon: 'IconCopy',
                        onClick: () => {
                          navigator.clipboard?.writeText(r.id);
                          toast(`已复制 ${r.id}`, { tone: 'pos', icon: 'IconCopy' });
                        },
                      },
                    ]}
                  >
                    <button className="btn btn-ghost btn-icon" style={{ width: 22, height: 22 }}>
                      <Icons.IconMoreV size={12} />
                    </button>
                  </ContextMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {/* offset 分页：真实 total → totalMode="exact" 显示总数 / 总页数 + 跳任意页 */}
        <Pagination
          total={refundTotal}
          page={page}
          pageSize={pageSize}
          onChange={setPage}
          totalMode="exact"
        />
      </div>

      {detail && <RefundDetail refund={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

// ADM-FIN-S004 · BONUS 发放（接真表单见 GrantBonusForm；reason_i18n en 必填，展示语言由 server 派生）
function BonusGrant() {
  return (
    <div className="vstack" style={{ gap: 12 }}>
      <div className="card" style={{ padding: 18 }}>
        <div
          style={{
            padding: '0 0 12px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, fontSize: 13.5 }}>主动发放 BONUS</div>
            <div style={{ fontSize: 11.5, color: 'var(--fg-muted)' }}>
              主动发放的 BONUS 有效期 30 天 · 默认原因「用户补偿」· 发放原因 reason_i18n
              多语言录入（en 必填）· 展示语言由 server 按用户最近语言派生
            </div>
          </div>
          {/* BONUS 发放历史：契约无端点 → disabled + NOT-in-scope（不假成功 toast） */}
          <button
            className="btn btn-sm"
            disabled
            title={NOT_IN_SCOPE_TITLE}
            style={{ cursor: 'not-allowed' }}
          >
            <Icons.IconHistory size={11} />
            历史记录
          </button>
        </div>
        <div style={{ paddingTop: 14 }}>
          <GrantBonusForm />
        </div>
      </div>
    </div>
  );
}

// ADM-FIN-002 · 产品销量统计（by_currency 分组真值）
function ProductSalesTab({
  currency,
  currencies,
  setCcy,
  app,
}: {
  currency: string;
  currencies: string[];
  setCcy: (c: string) => void;
  app: { id: string; name: string };
}) {
  // 原币不归一（#119）：取当前选中币种组的产品销量行（缺则空）
  const rows: ProductSales[] = useFinance().data.byCurrency[currency]?.productSales ?? [];
  const sym = currencySymbol(currency);
  const totalRev = rows.reduce((s, r) => s + r.revenue, 0);
  const totalOrders = rows.reduce((s, r) => s + r.orders, 0);
  const [kindFilter, setKindFilter] = useState<string[]>([]);
  const list = rows.filter((r) => !kindFilter.length || kindFilter.includes(r.kind));
  const top1 = rows[0];

  return (
    <div className="vstack" style={{ gap: 12 }}>
      <CurrencyTabs currencies={currencies} ccy={currency} setCcy={setCcy} />
      <ScopeBanner app={app} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12 }}>
        {[
          { label: '全部产品', v: String(rows.length), code: undefined as string | undefined },
          { label: '累计订单', v: totalOrders.toLocaleString(), code: undefined },
          { label: '累计收入', v: `${sym}${fmtInt(totalRev)}`, code: currency },
          {
            label: '平均客单价',
            v: totalOrders > 0 ? `${sym}${(totalRev / totalOrders).toFixed(2)}` : '—',
            code: currency,
          },
          { label: 'TOP1 占比', v: `${top1 ? top1.sharePct.toFixed(1) : '0.0'}%`, code: undefined },
        ].map((s, i) => (
          <div key={i} className="card" style={{ padding: 14 }}>
            <div className="stat-label">
              {s.label}
              {s.code && (
                <span
                  className="mono"
                  style={{ marginLeft: 5, fontSize: 9.5, color: 'var(--fg-faint)' }}
                >
                  {s.code}
                </span>
              )}
            </div>
            <div className="stat-value" style={{ fontSize: 22, marginTop: 4 }}>
              {s.v}
            </div>
          </div>
        ))}
      </div>

      <div
        className="card"
        style={{ padding: 14, background: 'var(--info-soft)', borderColor: 'transparent' }}
      >
        <div className="hstack" style={{ gap: 10 }}>
          <Icons.IconInfo size={14} style={{ color: 'var(--info)' }} />
          <span style={{ fontSize: 12, color: 'var(--fg)' }}>
            ADM-FIN-S006 · 同 <span className="mono">vendor_product_id</span> 跨 App 汇总为一行 ·
            按成交计（退款不冲减）· 「类型」由 <span className="mono">event_type</span> 推导（充值 /
            订阅）· by_currency 分组，当前 {currency}
          </span>
          <div style={{ flex: 1 }} />
          <FilterButton
            label="类型"
            width={130}
            value={kindFilter}
            onChange={setKindFilter}
            options={[
              { value: '充值', label: '充值' },
              { value: '订阅', label: '订阅' },
            ]}
          />
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <Table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 36 }}>#</th>
              <th>产品名称</th>
              <th>类型</th>
              <th>订阅周期</th>
              <th>币种</th>
              <th className="num">价格</th>
              <th className="num">购买次数</th>
              <th className="num">独立用户数</th>
              <th className="num">累计收入</th>
              <th className="num">收入占比</th>
              <th className="num">平均客单价</th>
              <th style={{ width: 90 }}>占比</th>
            </tr>
          </thead>
          <tbody>
            {list.map((r, i) => (
              <tr key={i}>
                <td className="mono muted">{String(i + 1).padStart(2, '0')}</td>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td>
                  <span
                    className={`chip ${r.kind === '订阅' ? 'chip-accent' : ''}`}
                    style={{ fontSize: 10 }}
                  >
                    {r.kind}
                  </span>
                </td>
                <td className="mono" style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
                  {r.period || '—'}
                </td>
                <td>
                  <span
                    className="chip mono"
                    style={{ fontSize: 10 }}
                    title="ISO 4217 · 原币不归一"
                  >
                    {currency}
                  </span>
                </td>
                <td className="num">{r.price != null ? `${sym}${r.price.toFixed(2)}` : '—'}</td>
                <td className="num">{r.orders.toLocaleString()}</td>
                <td className="num">{r.users.toLocaleString()}</td>
                <td className="num">
                  {sym}
                  {fmtInt(r.revenue)}
                </td>
                <td className="num mono">{r.sharePct.toFixed(1)}%</td>
                <td className="num">
                  {sym}
                  {r.aov.toFixed(2)}
                </td>
                <td>
                  <div
                    style={{
                      height: 6,
                      borderRadius: 3,
                      background: 'var(--bg-subtle)',
                      position: 'relative',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${Math.min(100, r.sharePct * 3.5)}%`,
                        background: r.kind === '订阅' ? 'var(--accent)' : 'var(--chart-2)',
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <div
          className="hstack"
          style={{
            padding: '8px 12px',
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-subtle)',
          }}
        >
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-faint)' }}>
            响应硬上限 200 行 · 按收入倒序 · 超出仅展示 TOP 200
          </span>
        </div>
      </div>
    </div>
  );
}
